import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Chip,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Avatar,
  CardActionArea,
  Badge,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  Payment as PaymentIcon,
  LocalPrintshop as PrintIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  AddCircleOutline as AddCircleIcon,
  Storefront as StorefrontIcon,
  Backspace as BackspaceIcon,
  Close as CloseIcon,
  AssignmentReturn as ReturnIcon,
} from '@mui/icons-material';
import { Maximize, Minimize } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as indexedDBService from '../utils/indexedDBService';
import { getTenantDataService } from '../services/tenant-data-service';
import { formatDate, formatCurrency } from '../utils/formatters';
import InvoicePrinter from '../utils/invoicePrinter';
import AnimatedPage from '../components/AnimatedPage';
import NewClientDialog from '../components/dialogs/NewClientDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';

interface CartItem {
  id: string;
  name: string;
  skuWithUnit: string;
  sellingPrice: number;
  purchasePrice: number;
  gstRate: number;
  quantity: number;
  stockQuantity: number;
  minStockLevel: number;
}

const POS: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const theme = useTheme();
  const db = React.useMemo(
    () => getTenantDataService(currentUser, userProfile),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser?.uid, (userProfile as any)?.tenantId]
  );
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // UI States
  const [isFullscreen, setIsFullscreen] = useState(false);
  const posContainerRef = useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  // Cart States
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [paymentMode, setPaymentMode] = useState<'cash' | 'upi' | 'card'>('cash');
  const [amountReceived, setAmountReceived] = useState<string>('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);

  // Return / Write-Off Modal — 3-step flow
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnStep, setReturnStep] = useState<1 | 2 | 3>(1);          // 1=invoice lookup, 2=item select, 3=condition
  const [invoiceInput, setInvoiceInput] = useState('');                  // bill number being typed
  const [invoiceLookupDone, setInvoiceLookupDone] = useState(false);
  const [eligibleItems, setEligibleItems] = useState<any[]>([]);         // 30-day items from the invoice
  const [returnItem, setReturnItem] = useState<any | null>(null);        // selected item to return
  const [returnQty, setReturnQty] = useState(1);
  const [returnReason, setReturnReason] = useState('');
  const [returnCondition, setReturnCondition] = useState<'restock' | 'writeoff'>('restock');
  const [isReturning, setIsReturning] = useState(false);
  const [recentOrders, setRecentOrders] = useState<any[]>([]);      // customer's recent orders for quick pick
  const [recentOrdersLoading, setRecentOrdersLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning'
  });

  // Fetch Inventory and Clients
  const fetchData = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      // Inventory is tenant-aware (DynamoDB for staff / IndexedDB for admin)
      const fetchedProducts = await db.getAll<any>('inventory');
      // Clients are always stored in local IndexedDB (Clients page not yet migrated to cloud)
      const fetchedClients = await indexedDBService.getAllItems<any>('clients');
      
      setProducts(fetchedProducts);
      setClients(fetchedClients);
    } catch (error) {
      console.error('Error loading POS data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading product catalogs',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser, db]);

  useEffect(() => {
    fetchData();
  }, [currentUser, fetchData]);

  // Fullscreen Logic
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Compute Categories and Quick Picks
  const categories = ['All', ...Array.from(new Set(products.map(p => p.category || 'General')))];
  
  // Quick Picks: First 8 items with highest stock turnover / stock quantity
  const quickPicks = [...products]
    .sort((a, b) => (b.stockQuantity || 0) - (a.stockQuantity || 0))
    .slice(0, 8);

  // Search filter
  const filteredProducts = products.filter(p => {
    const queryMatch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.skuWithUnit && p.skuWithUnit.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.hsnCode && p.hsnCode.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const categoryMatch = selectedCategory === 'All' || (p.category || 'General') === selectedCategory;
    
    return queryMatch && categoryMatch;
  });

  // Add Item to Cart
  const addToCart = useCallback((product: any) => {
    if (product.stockQuantity <= 0) {
      setSnackbar({
        open: true,
        message: `${product.name} is out of stock!`,
        severity: 'warning'
      });
      return;
    }

    setCart(prevCart => {
      const existing = prevCart.find(item => item.id === product.id);
      if (existing) {
        if (existing.quantity >= product.stockQuantity) {
          setSnackbar({
            open: true,
            message: `Cannot exceed available stock of ${product.stockQuantity} units!`,
            severity: 'warning'
          });
          return prevCart;
        }
        return prevCart.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [
        ...prevCart,
        {
          id: product.id,
          name: product.name,
          skuWithUnit: product.skuWithUnit || 'Nos',
          sellingPrice: product.sellingPrice || 0,
          purchasePrice: product.purchasePrice || 0,
          gstRate: product.gstRate || 0,
          quantity: 1,
          stockQuantity: product.stockQuantity,
          minStockLevel: product.minStockLevel || 0
        }
      ];
    });
  }, []);

  // Barcode Scanner Integration
  const handleBarcodeScan = useCallback((scannedCode: string) => {
    const matchedProduct = products.find(p => p.barcode === scannedCode);
    
    if (matchedProduct) {
      addToCart(matchedProduct);

      // Play native beep sound for feedback
      try {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(880, audioCtx.currentTime); // High pitch beep (A5)
        
        gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.1);

        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.1);
      } catch (err) {
        console.warn('Audio feedback failed:', err);
      }
    } else {
      setSnackbar({
        open: true,
        message: `Unknown barcode scanned: ${scannedCode}`,
        severity: 'warning'
      });
    }
  }, [products, addToCart]);

  useBarcodeScanner(handleBarcodeScan);

  // Adjust quantity in Cart
  const updateQuantity = (itemId: string, delta: number) => {
    setCart(prevCart =>
      prevCart
        .map(item => {
          if (item.id === itemId) {
            const nextQty = item.quantity + delta;
            if (nextQty > item.stockQuantity) {
              setSnackbar({
                open: true,
                message: `Cannot exceed available stock of ${item.stockQuantity} units!`,
                severity: 'warning'
              });
              return item;
            }
            return { ...item, quantity: nextQty };
          }
          return item;
        })
        .filter(item => item.quantity > 0)
    );
  };

  const removeFromCart = (itemId: string) => {
    setCart(prevCart => prevCart.filter(item => item.id !== itemId));
  };

  // Calculations
  const getCartTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    cart.forEach(item => {
      const taxable = (item.sellingPrice * item.quantity) / (1 + (item.gstRate / 100));
      const tax = (item.sellingPrice * item.quantity) - taxable;
      subtotal += taxable;
      totalTax += tax;
    });
    const total = subtotal + totalTax;
    return { subtotal, totalTax, total };
  };

  const { subtotal, totalTax, total } = getCartTotals();
  const changeDue = amountReceived ? Math.max(0, parseFloat(amountReceived) - total) : 0;

  // Checkout process
  const handleCheckout = async () => {
    if (cart.length === 0) {
      setSnackbar({ open: true, message: 'Your cart is empty!', severity: 'error' });
      return;
    }
    setCheckoutOpen(true);
    setAmountReceived(total.toFixed(2));
  };

  const confirmCheckout = async () => {
    if (!currentUser) return;
    setIsProcessing(true);
    try {
      const receiptNo = 'POS-' + Date.now();

      // 1. Deduct sold quantities from Inventory Management store
      for (const item of cart) {
        const allInv = await db.getAll<any>('inventory');
        const existingInventory = allInv.find((i: any) => i.id === item.id);
        if (existingInventory) {
          const updatedQty = Math.max(0, (existingInventory.stockQuantity || 0) - item.quantity);
          await db.put('inventory', {
            ...existingInventory,
            stockQuantity: updatedQty
          });
        }
      }

      // 1b. Save a POS order record so returns can be validated against purchase history
      const orderRecord = {
        id: receiptNo,
        userId: currentUser.uid,
        date: new Date().toISOString().split('T')[0],
        clientId: selectedClient?.id || 'walk-in',
        clientName: selectedClient?.name || 'Walk-In Customer',
        items: cart.map(item => ({ id: item.id, name: item.name, quantity: item.quantity, sellingPrice: item.sellingPrice })),
        total,
        createdAt: Date.now(),
      };
      await db.put('posOrders', orderRecord);

      // 2. Log payment as Inward entry in Cashbook
      const cashbookTransaction = {
        userId: currentUser.uid,
        date: new Date().toISOString().split('T')[0],
        description: `POS Checkout - Receipt #${receiptNo}`,
        amount: total,
        type: 'credit',
        category: 'POS Sales',
        reference: receiptNo,
        linkedEntityType: selectedClient ? 'client' : '',
        linkedEntityId: selectedClient?.id || '',
        linkedEntityName: selectedClient?.name || 'Walk-In Customer',
        createdAt: Date.now(),
        balance: 0 
      };
      await db.put('cashTransactions', { ...cashbookTransaction, id: receiptNo + '-tx' });

      // 3. Fetch active business profile for print header
      const businessProfiles = await db.getAll<any>('businesses');
      const activeBusiness = businessProfiles.find(b => b.userId === currentUser.uid) || {
        name: 'Akmo Books Retail',
        phone: '9999999999',
        email: 'info@akmobooks.com',
        address: 'Main Bazar, Retail Division',
        state: 'Tamil Nadu',
        gstRegistered: 'registered',
        gstType: 'regular',
        gstin: '33AABBC1234D1Z5'
      };

      // 4. Instantly generate and trigger the 3-inch Thermal Print PDF
      const clientDetails = selectedClient || {
        name: 'Walk-In Customer',
        phone: 'N/A',
        email: 'N/A',
        address: 'N/A',
        state: activeBusiness.state || 'Tamil Nadu'
      };

      const userSettings = await indexedDBService.getItemById<any>('userSettings', currentUser?.uid || '');
      const printCollectingCopy = userSettings?.invoiceSettings?.printCollectingCopy || false;

      const invoiceDetails = {
        invoiceNumber: receiptNo,
        invoiceDate: new Date().toISOString().split('T')[0],
        items: cart.map(item => ({
          name: item.name,
          description: item.skuWithUnit,
          quantity: item.quantity,
          rate: item.sellingPrice,
          gstRate: item.gstRate,
          amount: item.sellingPrice * item.quantity
        })),
        discount: 0,
        additionalCharges: [],
        termsAndConditions: ['Thank you for your purchase!'],
        placeOfSupply: clientDetails.state,
        printCollectingCopy: printCollectingCopy
      };

      await InvoicePrinter.printThermalInvoice(activeBusiness, clientDetails, invoiceDetails);

      setSnackbar({
        open: true,
        message: 'Checkout completed successfully! Receipt sent to thermal printer.',
        severity: 'success'
      });

      // Reset cart and dialog
      setCart([]);
      setSelectedClient(null);
      setCheckoutOpen(false);
      setAmountReceived('');
      fetchData(); 
    } catch (error) {
      console.error('POS Checkout failed:', error);
      setSnackbar({
        open: true,
        message: 'POS Checkout failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Return flow helpers ────────────────────────────────────────────────
  const resetReturnModal = () => {
    setReturnOpen(false);
    setReturnStep(1);
    setInvoiceInput('');
    setInvoiceLookupDone(false);
    setEligibleItems([]);
    setReturnItem(null);
    setReturnQty(1);
    setReturnReason('');
    setReturnCondition('restock');
    setRecentOrders([]);
  };

  /** Load recent orders for the selected customer when modal opens */
  useEffect(() => {
    if (!returnOpen || !currentUser) return;
    const loadRecentOrders = async () => {
      setRecentOrdersLoading(true);
      try {
        const allOrders = await db.getAll<any>('posOrders');
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - 30);

        const filtered = allOrders
          .filter((o: any) => {
            if (o.userId !== currentUser.uid) return false;
            // If a client is selected, filter to their orders; otherwise show all
            if (selectedClient && o.clientId !== selectedClient.id) return false;
            return new Date(o.createdAt) >= cutoffDate;
          })
          .sort((a: any, b: any) => b.createdAt - a.createdAt)
          .slice(0, 10);

        setRecentOrders(filtered);
      } catch (err) {
        console.error('Failed to load recent orders:', err);
      } finally {
        setRecentOrdersLoading(false);
      }
    };
    loadRecentOrders();
  }, [returnOpen, currentUser, selectedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Step 1 → Step 2: Look up posOrders by bill number, filter to 30 days */
  const lookupInvoice = async () => {
    if (!currentUser || !invoiceInput.trim()) return;
    setInvoiceLookupDone(false);
    try {
      const allOrders = await db.getAll<any>('posOrders');
      const order = allOrders.find(
        o => o.id?.toLowerCase() === invoiceInput.trim().toLowerCase() && o.userId === currentUser.uid
      );

      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - 30);

      if (!order || !Array.isArray(order.items)) {
        setEligibleItems([]);
        setInvoiceLookupDone(true);
        return;
      }

      const orderDate = new Date(order.createdAt);
      if (orderDate < cutoffDate) {
        setEligibleItems([]);
        setInvoiceLookupDone(true);
        return;
      }

      // Enrich items with purchase metadata and sort newest first
      const enriched = order.items
        .map((item: any) => ({
          ...item,
          purchaseDate: order.date,
          orderId: order.id,
          clientName: order.clientName || 'Walk-In Customer',
          orderCreatedAt: order.createdAt,
        }))
        .sort((a: any, b: any) => b.orderCreatedAt - a.orderCreatedAt);

      setEligibleItems(enriched);
      setInvoiceLookupDone(true);
    } catch (err) {
      console.error('Invoice lookup failed:', err);
      setEligibleItems([]);
      setInvoiceLookupDone(true);
    }
  };

  const processReturn = async () => {
    if (!currentUser || !returnItem) return;
    setIsReturning(true);
    try {
      // Find the actual inventory record by id
      const allInv = await db.getAll<any>('inventory');
      const existing = allInv.find((i: any) => i.id === returnItem.id);
      if (!existing) throw new Error('Item not found in inventory');

      if (returnCondition === 'restock') {
        await db.put('inventory', {
          ...existing,
          stockQuantity: (existing.stockQuantity || 0) + returnQty,
          returned_stock: (existing.returned_stock || 0) + returnQty,
          last_return_reason: returnReason || 'No reason provided',
          updatedAt: new Date().toISOString(),
        });
      } else {
        await db.put('inventory', {
          ...existing,
          damaged_stock: (existing.damaged_stock || 0) + returnQty,
          last_return_reason: returnReason || 'No reason provided',
          updatedAt: new Date().toISOString(),
        });
      }

      // Log refund transaction with negative amount
      const refundAmount = (returnItem.sellingPrice || 0) * returnQty;
      const refundTx = {
        userId: currentUser.uid,
        date: new Date().toISOString().split('T')[0],
        description: `POS Return - ${returnItem.name} x${returnQty} (${returnCondition === 'restock' ? 'Restocked' : 'Write-Off'}) | Bill: ${invoiceInput}${returnReason ? ` | Reason: ${returnReason}` : ''}`,
        amount: -refundAmount,
        type: 'refund',
        category: 'POS Returns',
        reference: 'RTN-' + Date.now(),
        createdAt: Date.now(),
        balance: 0
      };
      await db.put('cashTransactions', { ...refundTx, id: refundTx.reference });

      // Save dedicated return record
      const returnRecord = {
        id: 'ret_' + Date.now(),
        date: new Date().toISOString().split('T')[0],
        productId: returnItem.id,
        productName: returnItem.name,
        clientName: returnItem.clientName || 'Walk-In Customer',
        reason: returnReason || 'No reason provided',
        quantity: returnQty,
        totalValue: refundAmount,
        billNumber: invoiceInput,
        condition: returnCondition,
        userId: currentUser.uid,
        createdAt: Date.now()
      };
      await db.put('posReturns', returnRecord);

      setSnackbar({
        open: true,
        message: `✅ Return processed! ${returnQty}x ${returnCondition === 'restock' ? 'restocked.' : 'written off as damaged.'}`,
        severity: 'success'
      });
      resetReturnModal();
      fetchData();
    } catch (err) {
      console.error('Return failed:', err);
      setSnackbar({ open: true, message: 'Return processing failed. Please try again.', severity: 'error' });
    } finally {
      setIsReturning(false);
    }
  };

  // Numpad Handlers
  const handleNumpadPress = (val: string) => {
    if (val === 'clear') {
      setAmountReceived('');
    } else if (val === 'backspace') {
      setAmountReceived(prev => prev.slice(0, -1));
    } else {
      setAmountReceived(prev => {
        // Prevent multiple decimals
        if (val === '.' && prev.includes('.')) return prev;
        // Don't start with 00
        if (prev === '' && val === '00') return '0';
        // Max length constraint
        if (prev.length > 10) return prev;
        return prev + val;
      });
    }
  };

  return (
    <AnimatedPage>
      <Box 
        ref={posContainerRef} 
        sx={{ 
          ...(isFullscreen && {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1250,
            width: '100vw',
            height: '100vh',
            m: 0,
          }),
          p: isFullscreen ? 2 : 3, 
          height: isFullscreen ? '100vh' : 'auto', 
          overflow: isFullscreen ? 'hidden' : 'visible',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Page Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={isFullscreen ? 2 : 3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h4" fontWeight="bold" sx={{
              background: 'linear-gradient(45deg, #4F46E5 30%, #7C3AED 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Retail POS Terminal
            </Typography>
            <Chip 
              icon={<StorefrontIcon fontSize="small" />} 
              label="ONLINE • RETAIL ENGINE" 
              color="success" 
              size="small" 
              variant="outlined" 
              sx={{ fontWeight: 600, display: { xs: 'none', sm: 'flex' } }} 
            />
          </Box>
          <IconButton onClick={toggleFullscreen} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
            {isFullscreen ? <Minimize size={24} /> : <Maximize size={24} />}
          </IconButton>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ flexGrow: 1, height: isFullscreen ? 'calc(100% - 60px)' : 'auto' }}>
            
            {/* LEFT COLUMN (65%): Product Catalog & Quick Picks */}
            <Grid item xs={12} lg={7.5} sx={{ display: 'flex', flexDirection: 'column', height: isFullscreen ? '100%' : '80vh' }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  borderRadius: 4, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                {/* Search & Categories */}
                <Box mb={2}>
                  <TextField
                    placeholder="Search by Product Name, SKU, or HSN Code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                    size="medium"
                    sx={{ 
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.03)
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  
                  {/* Category Pills (Horizontal Scroll) */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      overflowX: 'auto', 
                      pb: 1,
                      '&::-webkit-scrollbar': { height: 6 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 3 }
                    }}
                  >
                    {categories.map((cat, idx) => (
                      <Chip 
                        key={idx}
                        label={cat}
                        onClick={() => setSelectedCategory(cat)}
                        color={selectedCategory === cat ? 'primary' : 'default'}
                        variant={selectedCategory === cat ? 'filled' : 'outlined'}
                        sx={{ 
                          fontWeight: 600, 
                          borderRadius: 2,
                          px: 1,
                          fontSize: '0.95rem',
                          height: 36
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Quick Picks / Frequently Billed */}
                {!searchQuery && selectedCategory === 'All' && quickPicks.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      🔥 Quick Picks
                    </Typography>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        overflowX: 'auto', 
                        pb: 1.5,
                        '&::-webkit-scrollbar': { display: 'none' }
                      }}
                    >
                      {quickPicks.map(product => (
                        <Card 
                          key={`qp-${product.id}`}
                          elevation={0}
                          onClick={() => addToCart(product)}
                          sx={{ 
                            minWidth: 140, 
                            maxWidth: 140, 
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                            cursor: 'pointer',
                            '&:hover': { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.05) }
                          }}
                        >
                          <CardActionArea sx={{ p: 1.5, height: '100%' }}>
                            <Typography variant="body2" fontWeight="bold" noWrap>
                              {product.name}
                            </Typography>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold" mt={0.5}>
                              {formatCurrency(product.sellingPrice)}
                            </Typography>
                          </CardActionArea>
                        </Card>
                      ))}
                    </Box>
                    <Divider sx={{ mt: 1 }} />
                  </Box>
                )}

                {/* Main Product Grid */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 0.5 }}>
                  <Grid container spacing={2}>
                    <AnimatePresence>
                      {filteredProducts.map(product => {
                        const isLowStock = product.stockQuantity <= (product.minStockLevel || 0);
                        return (
                          <Grid item xs={12} sm={6} md={4} xl={3} key={product.id}>
                            <motion.div
                              layout
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              transition={{ duration: 0.2 }}
                            >
                              <Card
                                elevation={0}
                                onClick={() => addToCart(product)}
                                sx={{
                                  border: '2px solid',
                                  borderColor: isLowStock ? alpha(theme.palette.error.main, 0.4) : alpha(theme.palette.divider, 0.5),
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  backgroundColor: isLowStock ? alpha(theme.palette.error.main, 0.03) : 'background.paper',
                                  transition: 'all 0.2s ease',
                                  height: '100%',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                    borderColor: theme.palette.primary.main
                                  }
                                }}
                              >
                                <CardActionArea sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                  <Box display="flex" justifyContent="space-between" width="100%" mb={1.5}>
                                    <Typography variant="subtitle1" fontWeight="700" sx={{ lineHeight: 1.2, maxWidth: '75%', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                      {product.name}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight="800" color="primary.main">
                                      {formatCurrency(product.sellingPrice || 0)}
                                    </Typography>
                                  </Box>
                                  
                                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" width="100%" mt="auto" pt={1}>
                                    {isLowStock ? (
                                      <Chip label={`${product.stockQuantity} Left`} size="small" sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, fontWeight: 700 }} />
                                    ) : (
                                      <Chip label={`${product.stockQuantity} In Stock`} size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.dark, fontWeight: 600 }} />
                                    )}
                                  </Box>
                                </CardActionArea>
                              </Card>
                            </motion.div>
                          </Grid>
                        );
                      })}
                    </AnimatePresence>
                    {filteredProducts.length === 0 && (
                      <Grid item xs={12}>
                        <Box textAlign="center" py={10} color="text.secondary">
                          <StorefrontIcon sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
                          <Typography variant="h6">No products found</Typography>
                          <Typography variant="body2">Try adjusting your search or category filter.</Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* RIGHT COLUMN (35%): Interactive Cart & Totals */}
            <Grid item xs={12} lg={4.5} sx={{ display: 'flex', flexDirection: 'column', height: isFullscreen ? '100%' : '80vh' }}>
              <Paper 
                elevation={6} 
                sx={{ 
                  borderRadius: 4, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  height: '100%',
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.08)'
                }}
              >
                {/* Client Selector Header */}
                <Box sx={{ p: 2.5, bgcolor: alpha(theme.palette.primary.main, 0.03), borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Box display="flex" gap={1} alignItems="center">
                    <Box sx={{ flexGrow: 1 }}>
                      <Autocomplete
                        options={clients}
                        getOptionLabel={(option) => `${option.name} (${option.phone || 'N/A'})`}
                        value={selectedClient}
                        onChange={(event, newValue) => setSelectedClient(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Customer..."
                            size="small"
                            sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>
                    <IconButton 
                      color="primary" 
                      onClick={() => setClientDialogOpen(true)}
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 2 }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Cart Items List */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#F8FAFC' }}>
                  {cart.length === 0 ? (
                    <Box textAlign="center" py={10} color="text.secondary" display="flex" flexDirection="column" alignItems="center">
                      <Avatar sx={{ width: 80, height: 80, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, mb: 2 }}>
                        <CartIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold">Cart is Empty</Typography>
                      <Typography variant="body2">Tap products to add them to the bill.</Typography>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      <AnimatePresence>
                        {cart.map((item) => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                          >
                            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                              <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="subtitle1" fontWeight="700" noWrap sx={{ maxWidth: '70%' }}>
                                  {item.name}
                                </Typography>
                                <Typography variant="subtitle1" fontWeight="700">
                                  {formatCurrency(item.sellingPrice * item.quantity)}
                                </Typography>
                              </Box>
                              
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="text.secondary">
                                  {formatCurrency(item.sellingPrice)} × {item.quantity}
                                </Typography>
                                
                                {/* Massive Touch Controls */}
                                <Box display="flex" alignItems="center" gap={0.5} sx={{ bgcolor: alpha(theme.palette.divider, 0.3), borderRadius: '50px', p: 0.5 }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => updateQuantity(item.id, -1)}
                                    sx={{ width: 36, height: 36, bgcolor: 'background.paper', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', color: theme.palette.error.main }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography sx={{ minWidth: 28, textAlign: 'center', fontWeight: '800' }}>{item.quantity}</Typography>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => updateQuantity(item.id, 1)}
                                    sx={{ width: 36, height: 36, bgcolor: 'background.paper', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', color: theme.palette.success.main }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Paper>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </Box>
                  )}
                </Box>

                {/* Cart Totals & Checkout CTA */}
                <Box sx={{ p: 3, bgcolor: 'background.paper', borderTop: `1px solid ${theme.palette.divider}` }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography color="text.secondary" fontWeight="600">Subtotal</Typography>
                    <Typography fontWeight="600">{formatCurrency(subtotal)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography color="text.secondary" fontWeight="600">Tax</Typography>
                    <Typography fontWeight="600">{formatCurrency(totalTax)}</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={3}>
                    <Typography variant="h6" fontWeight="bold">Total Due</Typography>
                    <Typography variant="h4" fontWeight="900" color="primary.main">{formatCurrency(total)}</Typography>
                  </Box>

                  {!selectedClient && (
                    <Typography variant="caption" color="text.secondary" textAlign="center" display="block" mb={1} fontWeight={600}>
                      👤 Select a customer above to enable checkout
                    </Typography>
                  )}
                  <Box display="flex" gap={1.5}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={() => setReturnOpen(true)}
                      disabled={!selectedClient}
                      startIcon={<ReturnIcon />}
                      sx={{
                        py: 1.8,
                        fontSize: '0.9rem',
                        borderRadius: 3,
                        fontWeight: '700',
                        borderColor: selectedClient ? 'error.main' : 'divider',
                        color: selectedClient ? 'error.main' : 'text.disabled',
                        opacity: selectedClient ? 1 : 0.5,
                        '&:hover': { bgcolor: selectedClient ? 'rgba(211,47,47,0.05)' : 'transparent' }
                      }}
                    >
                      Return
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={handleCheckout}
                      disabled={cart.length === 0 || !selectedClient}
                      startIcon={<PaymentIcon />}
                      sx={{
                        background: selectedClient && cart.length > 0
                          ? 'linear-gradient(45deg, #4F46E5 30%, #7C3AED 90%)'
                          : undefined,
                        color: 'white',
                        py: 1.8,
                        fontSize: '0.95rem',
                        borderRadius: 3,
                        fontWeight: '800',
                        opacity: selectedClient && cart.length > 0 ? 1 : 0.45,
                        boxShadow: selectedClient && cart.length > 0
                          ? '0 8px 25px rgba(79, 70, 229, 0.4)'
                          : 'none',
                        '&:hover': {
                          background: selectedClient && cart.length > 0
                            ? 'linear-gradient(45deg, #4338CA 30%, #6D28D9 90%)'
                            : undefined,
                          transform: selectedClient && cart.length > 0 ? 'translateY(-2px)' : 'none'
                        }
                      }}
                    >
                      Checkout
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* CHECKOUT NUMPAD MODAL */}
        <Dialog 
          open={checkoutOpen} 
          onClose={() => setCheckoutOpen(false)} 
          maxWidth="xs" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 4, bgcolor: '#F8FAFC' } }}
        >
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h5" fontWeight="900">Complete Payment</Typography>
              <IconButton onClick={() => setCheckoutOpen(false)} size="small" sx={{ bgcolor: alpha(theme.palette.divider, 0.5) }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ bgcolor: 'background.paper', p: 2.5, borderRadius: 3, mb: 3, boxShadow: '0 4px 15px rgba(0,0,0,0.03)' }}>
              <Typography color="text.secondary" fontWeight="600" variant="body2" mb={0.5}>TOTAL DUE</Typography>
              <Typography variant="h3" fontWeight="900" color="primary.main" mb={2}>
                {formatCurrency(total)}
              </Typography>
              
              <Box display="flex" gap={1}>
                {['cash', 'upi', 'card'].map((mode) => (
                  <Chip
                    key={mode}
                    label={mode.toUpperCase()}
                    onClick={() => setPaymentMode(mode as any)}
                    color={paymentMode === mode ? 'primary' : 'default'}
                    variant={paymentMode === mode ? 'filled' : 'outlined'}
                    sx={{ flexGrow: 1, fontWeight: 700, height: 36, borderRadius: 2 }}
                  />
                ))}
              </Box>
            </Box>

            {/* Custom Numpad Input Field */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, border: `2px solid ${theme.palette.primary.main}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h5" color="text.secondary">₹</Typography>
              <Typography variant="h4" fontWeight="bold">
                {amountReceived || '0'}
              </Typography>
            </Paper>

            {/* Touch Numpad Grid */}
            <Grid container spacing={1.5} mb={3}>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '00', '0', '.'].map((key) => (
                <Grid item xs={4} key={key}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleNumpadPress(key)}
                    sx={{ 
                      height: 64, 
                      fontSize: '1.5rem', 
                      fontWeight: '700', 
                      bgcolor: 'white', 
                      color: 'text.primary',
                      borderRadius: 3,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                      '&:hover': { bgcolor: '#F1F5F9' }
                    }}
                  >
                    {key}
                  </Button>
                </Grid>
              ))}
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleNumpadPress('clear')}
                  sx={{ height: 60, fontSize: '1.1rem', fontWeight: 'bold', bgcolor: '#F1F5F9', color: 'text.secondary', borderRadius: 3, boxShadow: 'none' }}
                >
                  CLEAR
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleNumpadPress('backspace')}
                  sx={{ height: 60, bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, borderRadius: 3, boxShadow: 'none' }}
                >
                  <BackspaceIcon />
                </Button>
              </Grid>
            </Grid>

            {/* Change Due Indicator */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), p: 2, borderRadius: 3, mb: 3 }}>
              <Typography fontWeight="700" color="success.dark">CHANGE DUE:</Typography>
              <Typography variant="h5" fontWeight="900" color="success.dark">{formatCurrency(changeDue)}</Typography>
            </Box>

            <Button
              onClick={confirmCheckout}
              variant="contained"
              fullWidth
              disabled={isProcessing || (parseFloat(amountReceived || '0') < total && paymentMode === 'cash')}
              startIcon={isProcessing ? <CircularProgress size={24} color="inherit" /> : <PrintIcon />}
              sx={{ 
                py: 2.5, 
                fontSize: '1.2rem', 
                fontWeight: '900',
                borderRadius: 3,
                bgcolor: '#059669', // Vibrant Emerald requested by user
                boxShadow: '0 8px 25px rgba(5, 150, 105, 0.4)',
                '&:hover': { bgcolor: '#047857' } 
              }}
            >
              {isProcessing ? 'PROCESSING...' : 'CONFIRM & PRINT'}
            </Button>
          </Box>
        </Dialog>

        {/* Feedback Alert Snackbar */}
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: 2, fontWeight: 'bold' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <NewClientDialog
          open={clientDialogOpen}
          onClose={() => setClientDialogOpen(false)}
          onSave={(newClient) => {
            setClients(prev => [newClient, ...prev]);
            setSelectedClient(newClient);
            setClientDialogOpen(false);
            setSnackbar({ open: true, message: `Customer "${newClient.name}" selected!`, severity: 'success' });
          }}
          client={null}
          userId={currentUser?.uid || ''}
        />

        {/* ─── Returns & Write-Off Modal ─── */}
        <Dialog
          open={returnOpen}
          onClose={resetReturnModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ pb: 0 }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ bgcolor: 'rgba(211,47,47,0.12)', color: 'error.main' }}>
                <ReturnIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="800">Process Return / Write-Off</Typography>
                <Typography variant="body2" color="text.secondary">
                  {returnStep === 1 ? 'Step 1: Enter bill number to look up purchase' : returnStep === 2 ? 'Step 2: Select item to return' : 'Step 3: Choose item condition'}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ pt: 2 }}>

            {/* ── Step 1: Invoice Lookup ── */}
            {returnStep === 1 && (
              <>
                <TextField
                  fullWidth
                  autoFocus
                  placeholder="Enter bill number (e.g. POS-1234567890)..."
                  value={invoiceInput}
                  onChange={(e) => setInvoiceInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && lookupInvoice()}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: invoiceInput ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => { setInvoiceInput(''); setInvoiceLookupDone(false); setEligibleItems([]); }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{ mb: 1.5 }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  onClick={lookupInvoice}
                  disabled={!invoiceInput.trim()}
                  sx={{ borderRadius: 2, fontWeight: 700, mb: 2 }}
                >
                  Look Up Invoice
                </Button>

                {/* ── Recent Orders from this customer ── */}
                <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                    {selectedClient ? `🕐 RECENT ORDERS — ${selectedClient.name.toUpperCase()}` : '🕐 RECENT ORDERS (ALL)'}
                  </Typography>
                  {recentOrdersLoading && <CircularProgress size={14} />}
                </Box>

                {!recentOrdersLoading && recentOrders.length === 0 && (
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.divider, 0.3), textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No purchases found within the last 30 days.</Typography>
                  </Box>
                )}

                <Box display="flex" flexDirection="column" gap={1} sx={{ maxHeight: 280, overflowY: 'auto' }}>
                  {recentOrders.map((order: any) => (
                    <Paper
                      key={order.id}
                      elevation={0}
                      onClick={async () => {
                        setInvoiceInput(order.id);
                        setInvoiceLookupDone(false);
                        setEligibleItems([]);
                        // Auto-trigger lookup
                        const cutoffDate = new Date();
                        cutoffDate.setDate(cutoffDate.getDate() - 30);
                        const orderDate = new Date(order.createdAt);
                        if (Array.isArray(order.items) && orderDate >= cutoffDate) {
                          const enriched = order.items
                            .map((item: any) => ({
                              ...item,
                              purchaseDate: order.date,
                              orderId: order.id,
                              orderCreatedAt: order.createdAt,
                            }))
                            .sort((a: any, b: any) => b.orderCreatedAt - a.orderCreatedAt);
                          setEligibleItems(enriched);
                          setInvoiceLookupDone(true);
                        }
                      }}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1.5px solid',
                        borderColor: invoiceInput === order.id ? 'primary.main' : 'divider',
                        bgcolor: invoiceInput === order.id ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.light', bgcolor: alpha(theme.palette.primary.main, 0.03) }
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography variant="body2" fontWeight={700}>{order.id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            📅 {new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            &nbsp;|&nbsp; {order.items?.length ?? 0} item{(order.items?.length ?? 0) !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body2" fontWeight={800} color="primary.main">
                            ₹{(order.total || 0).toFixed(2)}
                          </Typography>
                          {invoiceInput === order.id && invoiceLookupDone && eligibleItems.length > 0 && (
                            <Chip label="Ready" color="success" size="small" sx={{ fontWeight: 700, mt: 0.5 }} />
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>

                {invoiceLookupDone && eligibleItems.length === 0 && (
                  <Box sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(211,47,47,0.05)', border: '1px dashed rgba(211,47,47,0.3)', textAlign: 'center', mt: 1.5 }}>
                    <Typography color="error.main" fontWeight="600">No eligible items found for bill "{invoiceInput}".</Typography>
                    <Typography variant="caption" color="text.secondary">Returns are only valid within 30 days of purchase.</Typography>
                  </Box>
                )}
                {invoiceLookupDone && eligibleItems.length > 0 && (
                  <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: 'rgba(5,150,105,0.06)', border: '1px solid rgba(5,150,105,0.25)', mt: 1.5 }}>
                    <Typography variant="body2" fontWeight={700} color="success.dark">
                      ✅ {eligibleItems.length} item{eligibleItems.length > 1 ? 's' : ''} found. Click "Next" to select.
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* ── Step 2: Item Selection ── */}
            {returnStep === 2 && (
              <>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={1}>SELECT ITEM TO RETURN</Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {eligibleItems.map((item: any) => (
                    <Paper
                      key={item.id}
                      elevation={0}
                      onClick={() => { setReturnItem(item); setReturnQty(1); setReturnReason(''); }}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1.5px solid',
                        borderColor: returnItem?.id === item.id ? 'primary.main' : 'divider',
                        bgcolor: returnItem?.id === item.id ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.light' }
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography fontWeight={700}>{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            📅 Purchased: {item.purchaseDate ? new Date(item.purchaseDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                            &nbsp;|&nbsp; Qty: {item.quantity} &nbsp;|&nbsp; ₹{item.sellingPrice} each
                          </Typography>
                        </Box>
                        {returnItem?.id === item.id && <Chip label="Selected" color="primary" size="small" sx={{ fontWeight: 700 }} />}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </>
            )}

            {/* ── Step 3: Details & Condition ── */}
            {returnStep === 3 && returnItem && (
              <>
                <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, border: '2px solid', borderColor: 'success.main', bgcolor: 'rgba(5,150,105,0.04)' }}>
                  <Typography fontWeight="800" variant="subtitle1">{returnItem.name}</Typography>
                  <Typography variant="body2" color="text.secondary">Price: ₹{returnItem.sellingPrice} &nbsp;|&nbsp; Qty sold: {returnItem.quantity}</Typography>
                </Paper>

                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    label="Return Qty"
                    type="number"
                    value={returnQty}
                    onChange={(e) => setReturnQty(Math.min(returnItem.quantity, Math.max(1, parseInt(e.target.value) || 1)))}
                    inputProps={{ min: 1, max: returnItem.quantity }}
                    size="small"
                    sx={{ width: 100 }}
                  />
                  <TextField
                    label="Reason for Return"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    size="small"
                    fullWidth
                    placeholder="e.g. Defective, wrong item, etc."
                  />
                </Box>

                <Typography variant="subtitle2" fontWeight="700" mb={1} color="text.secondary">ITEM CONDITION</Typography>

                <RadioGroup
                  value={returnCondition}
                  onChange={(e) => setReturnCondition(e.target.value as 'restock' | 'writeoff')}
                >
                  <Paper
                    elevation={0}
                    onClick={() => setReturnCondition('restock')}
                    sx={{
                      p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer',
                      border: '1.5px solid',
                      borderColor: returnCondition === 'restock' ? 'success.main' : 'divider',
                      bgcolor: returnCondition === 'restock' ? 'rgba(5,150,105,0.05)' : 'background.paper',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FormControlLabel
                      value="restock"
                      control={<Radio color="success" />}
                      label={
                        <Box>
                          <Typography fontWeight="700">✅ Restock (Good Condition)</Typography>
                          <Typography variant="caption" color="text.secondary">Item is undamaged. Return to active inventory stock.</Typography>
                        </Box>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Paper>
                  <Paper
                    elevation={0}
                    onClick={() => setReturnCondition('writeoff')}
                    sx={{
                      p: 1.5, borderRadius: 2, cursor: 'pointer',
                      border: '1.5px solid',
                      borderColor: returnCondition === 'writeoff' ? 'error.main' : 'divider',
                      bgcolor: returnCondition === 'writeoff' ? 'rgba(211,47,47,0.05)' : 'background.paper',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FormControlLabel
                      value="writeoff"
                      control={<Radio color="error" />}
                      label={
                        <Box>
                          <Typography fontWeight="700">🛑 Write-Off (Damaged)</Typography>
                          <Typography variant="caption" color="text.secondary">Item is damaged. Record as write-off. Stock stays unchanged.</Typography>
                        </Box>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Paper>
                </RadioGroup>
              </>
            )}

          </DialogContent>

          <DialogActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
            <Button onClick={resetReturnModal} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            {returnStep > 1 && (
              <Button onClick={() => setReturnStep((s) => (s - 1) as 1 | 2 | 3)} sx={{ borderRadius: 2 }}>
                Back
              </Button>
            )}
            {returnStep === 1 && (
              <Button
                variant="contained"
                disabled={!invoiceLookupDone || eligibleItems.length === 0}
                onClick={() => setReturnStep(2)}
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                Next
              </Button>
            )}
            {returnStep === 2 && (
              <Button
                variant="contained"
                disabled={!returnItem}
                onClick={() => setReturnStep(3)}
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                Next
              </Button>
            )}
            {returnStep === 3 && (
              <Button
                variant="contained"
                disabled={!returnItem || isReturning}
                onClick={processReturn}
                startIcon={isReturning ? <CircularProgress size={18} color="inherit" /> : <ReturnIcon />}
                sx={{
                  borderRadius: 2,
                  fontWeight: '800',
                  bgcolor: returnCondition === 'writeoff' ? 'error.main' : 'success.main',
                  '&:hover': { bgcolor: returnCondition === 'writeoff' ? 'error.dark' : 'success.dark' },
                  boxShadow: returnCondition === 'writeoff'
                    ? '0 6px 20px rgba(211,47,47,0.35)'
                    : '0 6px 20px rgba(5,150,105,0.35)',
                }}
              >
                {isReturning ? 'Processing...' : 'Confirm Refund'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </AnimatedPage>
  );
};

export default POS;
